/**
 * Slot-machine reel. Purely cosmetic: the winner is decided before `spin` is
 * called, and the reel is built so that it stops exactly on that winner.
 */
import { secureRandomInt } from '../lib/random.js';
import { el, prefersReducedMotion } from '../ui/dom.js';
import { animate, easeOutCubic } from './easing.js';

/** The reel row as plain data, for the recap video (see recap/compositor.js). */
const rowData = (entry, extra = {}) => ({
  name: entry ? entry.displayName || entry.username : '',
  user: entry?.username ? `@${entry.username}` : '',
  ...extra,
});

function row(entry, className = '') {
  return el('li', { class: `slot-row ${className}` }, [
    el('p', { class: 'slot-name', text: entry ? entry.displayName || entry.username : '' }),
    el('p', { class: 'slot-user', text: entry?.username ? `@${entry.username}` : '' }),
  ]);
}

function placeholderRow(title, subtitle) {
  return el('li', { class: 'slot-row is-placeholder' }, [
    el('p', { class: 'slot-name', text: title }),
    el('p', { class: 'slot-user', text: subtitle }),
  ]);
}

export function createSlotView({ reel, frame, onFrame = () => {} }) {
  const rowHeight = () => frame.clientHeight / 3 || 76;
  // Idle displays are three rows with the centre one (index 1) showing.
  const publishIdle = (centreRow) => onFrame({ mode: 'slot', rows: [rowData(null), centreRow, rowData(null)], centre: 1, landed: true });

  function reset(rows) {
    reel.style.transform = 'translate3d(0,0,0)';
    reel.style.filter = '';
    reel.replaceChildren(...rows);
  }

  return {
    /** Idle display (centre row is index 1). */
    showMessage(title, subtitle = '') {
      reset([row(null), placeholderRow(title, subtitle), row(null)]);
      publishIdle({ name: title, user: subtitle, placeholder: true });
    },

    showWinner(entry) {
      reset([row(null), row(entry, 'is-winner'), row(null)]);
      publishIdle(rowData(entry, { winner: true }));
    },

    /**
     * @param {{pool: object[], winner: object, duration: number, onTick?: () => void}} options
     */
    async spin({ pool, winner, duration, onTick }) {
      // ~5 names/second on average (peaks ~15/s with the cubic ease), so names stay readable.
      const count = Math.min(60, Math.max(12, Math.round((duration / 1000) * 5)));
      const strip = [];
      for (let i = 0; i < count; i++) {
        let pick = pool[secureRandomInt(pool.length)];
        // Avoid showing the same name twice in a row when there's a choice.
        if (pool.length > 1 && pick === strip[i - 1]) pick = pool[(pool.indexOf(pick) + 1) % pool.length];
        strip.push(pick);
      }
      const winnerIndex = count - 2;
      strip[winnerIndex] = winner;

      const rows = strip.map((entry) => row(entry));
      reset(rows);
      const data = strip.map((entry) => rowData(entry));

      const h = rowHeight();
      const startCentre = 1;
      const distance = (winnerIndex - startCentre) * h;
      const blur = !prefersReducedMotion();
      let lastIndex = startCentre;
      let lastY = 0;
      let lastTime = performance.now();

      await animate(duration, (t, now) => {
        const y = easeOutCubic(t) * distance;
        reel.style.transform = `translate3d(0, ${-y}px, 0)`;

        onFrame({ mode: 'slot', rows: data, centre: startCentre + y / h, landed: false });

        const index = startCentre + Math.round(y / h);
        if (index !== lastIndex) {
          lastIndex = index;
          onTick?.();
        }
        if (blur) {
          const rowsPerSecond = (Math.abs(y - lastY) / Math.max(1, now - lastTime)) * (1000 / h);
          reel.style.filter = rowsPerSecond > 10 ? `blur(${Math.min(3, rowsPerSecond / 16).toFixed(2)}px)` : '';
        }
        lastY = y;
        lastTime = now;
      });

      reel.style.filter = '';
      data[winnerIndex].winner = true;
      onFrame({ mode: 'slot', rows: data, centre: winnerIndex, landed: true });
      rows[winnerIndex].classList.add('is-winner');
      frame.classList.remove('thunk');
      void frame.offsetWidth; // restart the CSS animation
      frame.classList.add('thunk');
    },
  };
}
